import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { useCustomerInsightQuery } from "@/v2-backoffice-client/customer/insight/customer.insight.query"
import { deriveCompanyIndicators } from "@/v2-backoffice-client/customer/insight/customer.insight.utils"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportFindings from "@/v2-backoffice-client/usage/report/UsageReportFindings"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"

export type CustomerCompanyInsightProps = {
  customer: CustomerFragment
}

// Company context on the Activity overview: the registry facts behind the
// customer's organisation number (v2-core-db) and where they meet the
// subscription — company size against licenses, renewal urgency, the
// real-estate footprint the product maps onto. Renders nothing when the
// customer carries no organisation number or the register has no match.
function CustomerCompanyInsight(props: CustomerCompanyInsightProps) {
  const { customer } = props
  const organizationNumber = customer.OrganizationNumber ?? ""

  const { data, loading, error } = useCustomerInsightQuery({
    skip: !organizationNumber,
    variables: { organizationNumber },
  })

  const profile = data?.getCompanyProfileByOrganizationNumber
  const indicators = useMemo(
    () => (profile ? deriveCompanyIndicators(profile, customer) : []),
    [profile, customer],
  )

  if (!organizationNumber) return null

  if (loading && !profile) {
    return (
      <p className="text-sm text-muted-foreground">Loading company profile…</p>
    )
  }

  if (error) {
    return (
      <p className="text-xs text-muted-foreground">
        Company registry lookup unavailable: {error.message}
      </p>
    )
  }

  // A registry miss is not a fact about the account; the Overview reads
  // better without a line saying so.
  if (!profile) return null

  const facts: { label: string; value: string | null }[] = [
    {
      label: "Industry",
      value:
        profile.IndustrialClassificationName ??
        profile.IndustrialSectorName ??
        null,
    },
    {
      label: "Employees",
      value:
        profile.NumberOfEmployees != null
          ? formatNumber(profile.NumberOfEmployees)
          : null,
    },
    { label: "Headquarters", value: profile.HeadquartersCity ?? null },
    {
      label: "Founded",
      value: profile.FoundedAt ? formatDateString(profile.FoundedAt) : null,
    },
    {
      label: "Owned properties",
      value:
        profile.OwnedPropertiesCount != null && profile.OwnedPropertiesCount > 0
          ? formatNumber(profile.OwnedPropertiesCount)
          : null,
    },
    {
      label: "Subsidiaries",
      value:
        profile.SubsidiaryCount != null && profile.SubsidiaryCount > 0
          ? formatNumber(profile.SubsidiaryCount)
          : null,
    },
  ].filter((fact) => fact.value != null)

  return (
    <UsageReportSection
      eyebrow="Company"
      title={profile.Name ?? "The company behind the account"}
      lead={
        <>
          Organisation number {profile.OrganizationNumber}, from the company
          register.
          {profile.BusinessActivity && <> {profile.BusinessActivity}</>}
        </>
      }
    >
      <div className="usage-report-grid">
        {facts.length > 0 && (
          <UsageReportCard title="Facts">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
              {facts.map((fact) => (
                <div key={fact.label} className="flex flex-col">
                  <dt className="text-xs text-muted-foreground">
                    {fact.label}
                  </dt>
                  <dd className="text-sm tabular-nums">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </UsageReportCard>
        )}
        {indicators.length > 0 && (
          <UsageReportCard
            title="What it means for the account"
            caption="Where the company's size and footprint meet the subscription."
          >
            <UsageReportFindings
              findings={indicators.map((indicator) => ({
                key: indicator.text,
                text: indicator.text,
                tone: indicator.tone,
              }))}
            />
          </UsageReportCard>
        )}
      </div>
    </UsageReportSection>
  )
}

export default CustomerCompanyInsight
